<script setup>
import { computed, reactive } from 'vue'

const props = defineProps({ assets: { type: Array, default: () => [] } })

const options = reactive({
  id: props.assets[0]?.id ?? null,
  w: 800,
  h: null,
  rt: 'fit',
  f: 'webp',
  // video rendition controls
  vformat: 'original',
  vheight: 'original',
})

const assetItems = computed(() =>
  props.assets.map((a) => ({
    label: `ID ${a.id} — ${a.filename}${a.kind === 'video' ? ' · video' : ''}`,
    value: a.id,
  })),
)

const selected = computed(() => props.assets.find((a) => a.id === options.id))
const isVideo = computed(() => selected.value?.kind === 'video')

// The video URL. /assets/:id/img returns the POSTER for a video, so the clip
// itself is reached via /file. With a format chosen it serves a rendition
// (resized/re-encoded); "Original" = the untouched upload.
const videoFormatItems = [
  { label: 'Original (no transcode)', value: 'original' },
  { label: 'mp4 (H.264)', value: 'mp4' },
  { label: 'webm (VP9)', value: 'webm' },
]
const videoHeightItems = [
  { label: 'Original height', value: 'original' },
  { label: '480p', value: '480' },
  { label: '720p', value: '720' },
  { label: '1080p', value: '1080' },
]
const fileUrl = computed(() => {
  if (!options.id) return ''
  const base = `${window.location.origin}/assets/${options.id}/file`
  if (options.vformat === 'original') return base
  const params = new URLSearchParams({ f: options.vformat })
  if (options.vheight !== 'original') params.set('h', options.vheight)
  return `${base}?${params}`
})
const resizeItems = [
  { label: 'fit — shrink to fit inside w×h', value: 'fit' },
  { label: 'fill — crop to fill w×h exactly', value: 'fill' },
  { label: 'force — stretch to w×h', value: 'force' },
]
const formatItems = ['webp', 'avif', 'jpg', 'png']

// The generated URL points at the Rails on-demand endpoint, which signs a
// fresh imgproxy URL per request — so this link is stable and shareable.
// Params matching the endpoint defaults are omitted to keep the URL short.
const url = computed(() => {
  if (!options.id) return ''
  const params = new URLSearchParams({ w: options.w || 800 })
  if (options.h) params.set('h', options.h)
  if (options.rt !== 'fit') params.set('rt', options.rt)
  if (options.f !== 'webp') params.set('f', options.f)
  return `${window.location.origin}/assets/${options.id}/img?${params}`
})

// One flag per field so the two copy buttons toggle "Copied!" independently.
const copied = reactive({ img: false, file: false })
async function copy(text, key) {
  try {
    await navigator.clipboard.writeText(text)
    copied[key] = true
    setTimeout(() => (copied[key] = false), 1500)
  } catch {
    // clipboard API can be unavailable (permissions) — leave the URL selectable
  }
}
</script>

<template>
  <UCard class="mx-auto max-w-2xl">
    <template #header>
      <h2 class="font-semibold">Transform URL generator</h2>
      <p class="text-sm text-muted">Build an on-demand resize URL for any asset.</p>
    </template>

    <div v-if="assets.length === 0" class="text-muted">
      No assets yet —
      <UButton to="/assets/new" variant="link" label="upload an image first" />
    </div>

    <div v-else class="space-y-6">
      <UFormField
        :label="isVideo ? 'Poster image URL' : 'Your URL'"
        :hint="isVideo ? 'imgproxy transform of the video’s poster frame' : undefined"
      >
        <div class="flex gap-2">
          <UInput :model-value="url" readonly class="grow font-mono" :ui="{ base: 'text-xs' }" />
          <UButton
            :icon="copied.img ? 'i-lucide-check' : 'i-lucide-clipboard'"
            :color="copied.img ? 'success' : 'primary'"
            :label="copied.img ? 'Copied!' : 'Copy'"
            @click="copy(url, 'img')"
          />
        </div>
      </UFormField>

      <div v-if="isVideo" class="space-y-3 rounded-lg border border-default p-4">
        <UFormField label="Video URL" hint="the clip itself (resize / re-encode below)">
          <div class="flex gap-2">
            <UInput :model-value="fileUrl" readonly class="grow font-mono" :ui="{ base: 'text-xs' }" />
            <UButton
              :icon="copied.file ? 'i-lucide-check' : 'i-lucide-clipboard'"
              :color="copied.file ? 'success' : 'primary'"
              :label="copied.file ? 'Copied!' : 'Copy'"
              @click="copy(fileUrl, 'file')"
            />
          </div>
        </UFormField>

        <div class="grid grid-cols-2 gap-4">
          <UFormField label="Format">
            <USelect v-model="options.vformat" :items="videoFormatItems" class="w-full" />
          </UFormField>
          <UFormField label="Resize" hint="downscale only">
            <USelect v-model="options.vheight" :items="videoHeightItems" :disabled="options.vformat === 'original'" class="w-full" />
          </UFormField>
        </div>

        <p v-if="options.vformat !== 'original'" class="text-xs text-muted">
          Renditions transcode on first request — the first open serves the original; reload once it's ready.
        </p>
      </div>

      <div class="grid grid-cols-2 gap-4">
        <UFormField label="Asset" class="col-span-2">
          <USelect v-model="options.id" :items="assetItems" class="w-full" />
        </UFormField>
        <!-- The server snaps sizes up to a 100px grid (max 1600) to keep the
             variant space small and cacheable — mirror that here -->
        <UFormField label="Width (px)" hint="100px steps">
          <UInput v-model.number="options.w" type="number" min="100" max="1600" step="100" class="w-full" />
        </UFormField>
        <UFormField label="Height (px)" hint="optional, 100px steps">
          <UInput v-model.number="options.h" type="number" min="100" max="1600" step="100" placeholder="auto" class="w-full" />
        </UFormField>
        <UFormField label="Resizing type">
          <USelect v-model="options.rt" :items="resizeItems" class="w-full" />
        </UFormField>
        <UFormField label="Format">
          <USelect v-model="options.f" :items="formatItems" class="w-full" />
        </UFormField>
      </div>

      <UFormField label="Live preview" hint="rendered by imgproxy right now">
        <img :src="url" alt="preview" class="max-h-80 rounded border border-default" />
      </UFormField>
    </div>
  </UCard>
</template>
